'use client'

/**
 * Flag a notetaker output as wrong (#14382, part of #14371).
 *
 * `note-output-flag-button` sits at the end of a final summary claim, an
 * item card and a live panel line. Pressing it opens a small reason picker —
 * the five `OUTPUT_FLAG_REASONS`, an optional comment of at most 500
 * characters — and saving calls `flagNoteOutput`. Afterwards the control
 * reads "Flagged"; pressing it again changes the flag (a repeat flag updates
 * the first, server-side).
 *
 * The request NAMES the target — note, kind, reference, and the recording
 * for a live line — and never carries the text: `targetLabel` is only the
 * accessible name and the dialog's quote. The server snapshots what the
 * notetaker actually stored.
 *
 * The testid is NOT keyed by target, and deliberately: the `ui` surface
 * declares this exact string, and the golden-path gate greps the tagged spec
 * for `getByTestId('note-output-flag-button')` as a literal. `data-target-ref`
 * is what tells two controls apart.
 *
 * Keyboard: a real `<button>` in the tab order with a 44px target; the picker
 * is the shared `Modal` (focus trap, Escape, focus restore). Nothing is
 * hover-only.
 *
 * ## Controlled open (#15624)
 *
 * An item card or decision on the note page opens this same picker from its
 * "…" menu (`NoteOutputFlagMenu`): pass `open` and `onOpenChange` and the
 * control renders no button of its own — only the picker, with the same
 * reasons, comment, save and server action. One dialog, two ways in.
 */

import { useId, useState } from 'react'

import { flagNoteOutput } from '@/app/actions/note-output-flags'
import { Flag, Loader2 } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Textarea } from '@/components/ui/textarea'
import {
  NOTE_OUTPUT_FLAG_COMMENT_MAX_CHARS,
  OUTPUT_FLAG_REASON_LABELS,
  OUTPUT_FLAG_REASONS,
  type OutputFlagReason,
  type OutputFlagTargetKind,
  parseOutputFlagReason,
  type ViewerOutputFlag,
} from '@/lib/notes/note-output-flags'
import { toastSuccess } from '@/lib/toast'
import { cn } from '@/lib/utils'

export interface NoteOutputFlagControlProps {
  noteId: string
  targetKind: OutputFlagTargetKind
  /** The claim id, the summary claim's `summaryClaimRef`, or the item id. */
  targetRef: string
  /** The live capture — a `live_claim` only. */
  recordingId?: string | null
  /** The flagged text, for the accessible name and the quote. Never sent. */
  targetLabel: string
  /**
   * This reader's earlier flag on the target, when there is one — its
   * comment too, so re-flagging edits the stored comment instead of
   * silently clearing it.
   */
  initialFlag?: ViewerOutputFlag | null
  /** Told of each save — the live panel keeps flags across remounts. */
  onFlagged?: (flag: ViewerOutputFlag) => void
  /**
   * Controlled open (#15624): set, the control renders no button and the
   * picker follows it; `onOpenChange(false)` asks the owner to close it.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
}

export function NoteOutputFlagControl({
  noteId,
  targetKind,
  targetRef,
  recordingId = null,
  targetLabel,
  initialFlag = null,
  onFlagged,
  open: controlledOpen,
  onOpenChange,
  className,
}: NoteOutputFlagControlProps) {
  const controlled = controlledOpen !== undefined
  const [flagged, setFlagged] = useState<ViewerOutputFlag | null>(initialFlag)
  const [ownOpen, setOwnOpen] = useState(false)
  const open = controlled ? controlledOpen : ownOpen
  const setOpen = (next: boolean) => {
    if (!controlled) setOwnOpen(next)
    onOpenChange?.(next)
  }
  const [reason, setReason] = useState<OutputFlagReason | null>(
    initialFlag?.reason ?? null,
  )
  const [comment, setComment] = useState(initialFlag?.comment ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const commentId = useId()
  const reasonGroupId = useId()

  // Every opening starts from the saved flag, however it was opened — set
  // during render, the documented way to react to a prop without an effect.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setReason(flagged?.reason ?? null)
      setComment(flagged?.comment ?? '')
      setError(null)
    }
  }

  const save = async () => {
    if (reason === null || saving) return
    setSaving(true)
    setError(null)
    try {
      const trimmed = comment.trim()
      const saved = {
        reason,
        comment: trimmed.length === 0 ? null : trimmed,
      }
      const result = await flagNoteOutput({
        noteId,
        targetKind,
        targetRef,
        recordingId: targetKind === 'live_claim' ? recordingId : null,
        ...saved,
      })
      if (!result.success) {
        setError(result.error ?? 'Could not save this flag')
        return
      }
      setFlagged(saved)
      onFlagged?.(saved)
      setOpen(false)
      toastSuccess('Flagged. Thanks — this helps the notes get better.')
    } catch {
      // Shown in the open picker, choices kept — see sub-issue R carve-out
      setError('Could not save this flag. Check your connection.')
    } finally {
      setSaving(false)
    }
  }

  const isFlagged = flagged !== null

  return (
    <>
      {controlled ? null : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="note-output-flag-button"
          data-target-kind={targetKind}
          data-target-ref={targetRef}
          data-flagged={isFlagged ? 'true' : 'false'}
          aria-label={
            isFlagged
              ? `Flagged as wrong: ${targetLabel}. Change the flag`
              : `Flag as wrong: ${targetLabel}`
          }
          className={cn(
            'pointer-coarse:min-h-11 pointer-coarse:min-w-11 shrink-0 gap-1.5 text-text-secondary',
            isFlagged && 'text-text-primary',
            className,
          )}
        >
          <Flag className="h-4 w-4" aria-hidden="true" />
          {isFlagged ? 'Flagged' : 'Flag'}
        </Button>
      )}

      <Modal
        isOpen={open}
        onClose={() => {
          if (!saving) setOpen(false)
        }}
        title="What's wrong with this?"
        size="sm"
        onSubmit={reason === null ? null : () => void save()}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              className="pointer-coarse:min-h-11"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void save()}
              disabled={reason === null || saving}
              aria-busy={saving}
              data-testid="note-output-flag-submit"
              className="pointer-coarse:min-h-11 gap-1.5"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              {isFlagged ? 'Update flag' : 'Flag as wrong'}
            </Button>
          </div>
        }
      >
        <div
          data-testid="note-output-flag-dialog"
          className="flex flex-col gap-4"
        >
          <blockquote className="border-l-2 border-stroke-light pl-3 text-sm text-text-secondary">
            {targetLabel}
          </blockquote>
          {/* Native radios with SYNCHRONOUS state, not `RadioCardGroup`: that
            group routes every change through `useTransition` for callers that
            save on select, and a transition is held back while anything in
            the tree suspends — so a pick could sit unapplied, the radios
            disabled, until an unrelated suspense settled. A local choice has
            no reason to be a transition. */}
          <fieldset
            data-testid="note-output-flag-reasons"
            className="flex flex-col gap-2"
          >
            <legend className="mb-2 text-sm font-medium text-text-primary">
              Reason
            </legend>
            {OUTPUT_FLAG_REASONS.map(option => {
              const optionId = `${reasonGroupId}-${option}`
              const selected = reason === option
              return (
                <label
                  key={option}
                  htmlFor={optionId}
                  className={cn(
                    'flex pointer-coarse:min-h-11 cursor-pointer items-center gap-3 rounded-md border border-stroke-regular bg-surface-elevated px-3 py-2 text-sm text-text-primary transition-colors',
                    selected && 'border-action-primary bg-surface-panel',
                    saving && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <input
                    id={optionId}
                    type="radio"
                    name={reasonGroupId}
                    value={option}
                    checked={selected}
                    disabled={saving}
                    onChange={() => setReason(parseOutputFlagReason(option))}
                    className="h-4 w-4 cursor-pointer accent-action-primary"
                  />
                  {OUTPUT_FLAG_REASON_LABELS[option]}
                </label>
              )
            })}
          </fieldset>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor={commentId}
              className="text-sm font-medium text-text-primary"
            >
              Add a comment (optional)
            </label>
            <Textarea
              id={commentId}
              value={comment}
              onChange={event => setComment(event.target.value)}
              maxLength={NOTE_OUTPUT_FLAG_COMMENT_MAX_CHARS}
              rows={3}
              disabled={saving}
              data-testid="note-output-flag-comment"
            />
            <p
              data-testid="note-output-flag-comment-count"
              className="self-end text-xs text-text-secondary"
            >
              {comment.length}/{NOTE_OUTPUT_FLAG_COMMENT_MAX_CHARS}
            </p>
          </div>
          {error !== null ? (
            <p role="alert" className="text-sm text-text-primary">
              {error}
            </p>
          ) : null}
        </div>
      </Modal>
    </>
  )
}
